import { useState } from 'react'
import { formatarCep, normalizarCep, validarCep } from '@precad/compartilhado/validacao/cep'
import { UFS } from '@precad/compartilhado/validacao/uf'
import { Campo, Selecao } from '../componentes/Campo'
import type { TipoCliente } from '../tipos'

/** Campos que esta etapa grava. Só eles vão no payload — ver regra 2 do hook. */
export const CAMPOS_EMPRESA = [
  'razaoSocial',
  'nomeFantasia',
  'inscricaoEstadual',
  'tipoClienteId',
  'tipoClienteOutro',
  'possuiFiliais',
  'quantidadeFiliais',
  'cep',
  'endereco',
  'numeroEndereco',
  'complemento',
  'bairro',
  'cidade',
  'uf',
]

interface Props {
  dados: Record<string, string>
  tiposCliente: TipoCliente[]
  aoAlterar: (campo: string, valor: string) => void
  aoDefinirVarios: (valores: Record<string, string>) => void
  aoVoltar: () => void
  aoAvancar: () => void
  ocupado: boolean
}

export function Empresa({
  dados,
  tiposCliente,
  aoAlterar,
  aoDefinirVarios,
  aoVoltar,
  aoAvancar,
  ocupado,
}: Props) {
  const [erros, setErros] = useState<Record<string, string>>({})
  const [buscandoCep, setBuscandoCep] = useState(false)

  const tipoEscolhido = tiposCliente.find((t) => String(t.id) === dados.tipoClienteId)
  const exigeTextoLivre = tipoEscolhido?.permiteTextoLivre === 1
  const temFiliais = dados.possuiFiliais === '1'

  /**
   * Autocompletar por CEP. Falha em silêncio de propósito: se o ViaCEP estiver
   * fora, ou a CSP barrar, a pessoa preenche à mão e não fica sabendo que algo
   * quebrou — a comodidade some, o formulário não.
   */
  async function buscarCep() {
    const cep = normalizarCep(dados.cep ?? '')
    if (!validarCep(cep)) return
    setBuscandoCep(true)
    try {
      const resposta = await fetch(`https://viacep.com.br/ws/${cep}/json/`, {
        signal: AbortSignal.timeout(6000),
      })
      const achado = (await resposta.json()) as {
        erro?: boolean | string
        logradouro?: string
        bairro?: string
        localidade?: string
        uf?: string
      }
      if (achado.erro) return
      /* Não sobrescreve o que a pessoa já digitou: quem mora em rua sem
         logradouro cadastrado corrige à mão, e perder isso irrita. */
      const preencher: Record<string, string> = {}
      if (achado.logradouro && !dados.endereco) preencher.endereco = achado.logradouro
      if (achado.bairro && !dados.bairro) preencher.bairro = achado.bairro
      if (achado.localidade && !dados.cidade) preencher.cidade = achado.localidade
      if (achado.uf && !dados.uf) preencher.uf = achado.uf
      if (Object.keys(preencher).length) aoDefinirVarios(preencher)
    } catch {
      /* silêncio deliberado */
    } finally {
      setBuscandoCep(false)
    }
  }

  function validar(): boolean {
    const novos: Record<string, string> = {}
    if (!dados.razaoSocial?.trim()) novos.razaoSocial = 'Informe a razão social.'
    if (!dados.nomeFantasia?.trim()) novos.nomeFantasia = 'Informe o nome fantasia.'
    if (!dados.tipoClienteId) novos.tipoClienteId = 'Escolha o tipo de cliente.'
    if (exigeTextoLivre && !dados.tipoClienteOutro?.trim()) {
      novos.tipoClienteOutro = 'Descreva o tipo de cliente.'
    }
    if (temFiliais && !dados.quantidadeFiliais?.trim()) {
      novos.quantidadeFiliais = 'Informe quantas filiais.'
    }
    if (!validarCep(normalizarCep(dados.cep ?? ''))) novos.cep = 'CEP inválido.'
    if (!dados.endereco?.trim()) novos.endereco = 'Informe o endereço.'
    if (!dados.numeroEndereco?.trim()) novos.numeroEndereco = 'Informe o número.'
    if (!dados.bairro?.trim()) novos.bairro = 'Informe o bairro.'
    if (!dados.cidade?.trim()) novos.cidade = 'Informe a cidade.'
    if (!dados.uf) novos.uf = 'Escolha o estado.'

    setErros(novos)
    if (Object.keys(novos).length) {
      document.querySelector<HTMLElement>('[aria-invalid="true"]')?.focus()
      return false
    }
    return true
  }

  const mudar = (campo: string) => (valor: string) => {
    aoAlterar(campo, valor)
    if (erros[campo]) setErros((e) => ({ ...e, [campo]: '' }))
  }

  return (
    <form
      noValidate
      onSubmit={(e) => {
        e.preventDefault()
        if (validar()) aoAvancar()
      }}
    >
      <div className="grade grade--duas">
        <Campo rotulo="Razão social" valor={dados.razaoSocial ?? ''} aoMudar={mudar('razaoSocial')} erro={erros.razaoSocial} largo maxLength={255} autoComplete="organization" />
        <Campo rotulo="Nome fantasia" valor={dados.nomeFantasia ?? ''} aoMudar={mudar('nomeFantasia')} erro={erros.nomeFantasia} maxLength={255} />
        <Campo rotulo="Inscrição estadual" valor={dados.inscricaoEstadual ?? ''} aoMudar={mudar('inscricaoEstadual')} erro={erros.inscricaoEstadual} opcional maxLength={50} dica="Deixe em branco se for isento." />

        <Selecao
          rotulo="Tipo de cliente"
          valor={dados.tipoClienteId ?? ''}
          aoMudar={(v) => {
            mudar('tipoClienteId')(v)
            /* Trocar o tipo limpa o texto livre: sem isso, quem escolhe
               "Outros", descreve, e depois muda para "Farmácia" leva a
               descrição órfã junto até a ficha e o ERP. */
            const novo = tiposCliente.find((t) => String(t.id) === v)
            if (!novo?.permiteTextoLivre && dados.tipoClienteOutro) aoAlterar('tipoClienteOutro', '')
          }}
          erro={erros.tipoClienteId}
          opcoes={tiposCliente.map((t) => ({ valor: String(t.id), texto: t.nome }))}
        />

        {exigeTextoLivre ? (
          <Campo rotulo="Qual?" valor={dados.tipoClienteOutro ?? ''} aoMudar={mudar('tipoClienteOutro')} erro={erros.tipoClienteOutro} largo maxLength={120} />
        ) : null}

        <Selecao
          rotulo="Possui outras filiais?"
          valor={dados.possuiFiliais ?? ''}
          aoMudar={(v) => {
            mudar('possuiFiliais')(v)
            if (v !== '1' && dados.quantidadeFiliais) aoAlterar('quantidadeFiliais', '')
          }}
          opcoes={[
            { valor: '1', texto: 'Sim' },
            { valor: '0', texto: 'Não' },
          ]}
          vazio="Selecione"
        />

        {temFiliais ? (
          <Campo rotulo="Quantas filiais?" valor={dados.quantidadeFiliais ?? ''} aoMudar={mudar('quantidadeFiliais')} erro={erros.quantidadeFiliais} modo="numeric" maxLength={4} />
        ) : null}
      </div>

      <h3 className="secao">Endereço</h3>

      <div className="grade grade--duas">
        <Campo
          rotulo="CEP"
          valor={dados.cep ?? ''}
          aoMudar={mudar('cep')}
          aoSair={() => {
            const cep = normalizarCep(dados.cep ?? '')
            if (validarCep(cep)) {
              aoAlterar('cep', formatarCep(cep))
              void buscarCep()
            }
          }}
          erro={erros.cep}
          modo="numeric"
          maxLength={9}
          autoComplete="postal-code"
          placeholder="00000-000"
          dica={buscandoCep ? 'Buscando endereço…' : 'Preenchemos o endereço automaticamente.'}
        />
        <Campo rotulo="Endereço" valor={dados.endereco ?? ''} aoMudar={mudar('endereco')} erro={erros.endereco} maxLength={255} autoComplete="address-line1" />
        <Campo rotulo="Número" valor={dados.numeroEndereco ?? ''} aoMudar={mudar('numeroEndereco')} erro={erros.numeroEndereco} maxLength={50} />
        <Campo rotulo="Complemento" valor={dados.complemento ?? ''} aoMudar={mudar('complemento')} opcional maxLength={255} />
        <Campo rotulo="Bairro" valor={dados.bairro ?? ''} aoMudar={mudar('bairro')} erro={erros.bairro} maxLength={255} />
        <Campo rotulo="Cidade" valor={dados.cidade ?? ''} aoMudar={mudar('cidade')} erro={erros.cidade} maxLength={250} />
        <Selecao rotulo="Estado" valor={dados.uf ?? ''} aoMudar={mudar('uf')} erro={erros.uf} opcoes={UFS.map((u) => ({ valor: u, texto: u }))} />
      </div>

      <div className="rodape">
        <button type="button" className="botao botao--secundario" onClick={aoVoltar}>
          Voltar
        </button>
        <span className="rodape__espaco" />
        <button type="submit" className="botao botao--principal" aria-disabled={ocupado}>
          {ocupado ? 'Salvando…' : 'Continuar'}
        </button>
      </div>
    </form>
  )
}
